import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyleProfileEditor } from './StyleProfileEditor';
import type { UseStyleIntelligenceReturn } from './useStyleIntelligence';
import { useStyleIntelligenceStore } from '@/stores/ai/style-intelligence-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { canManageStyleIntelligence } from '@/utils/organizationPermissions';
import { FOREST_GREEN, MID_GRAY } from '@/styles/tokens';
import { cn } from '@/lib/utils';
import { Upload, Globe, Layers, Loader2, Trash2, Power, PowerOff, Image as ImageIcon, Search, Download, TrendingUp, Clock, Share2, Lock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectPermission, ProjectRole, type IStyleProfile, type ProjectMember, type StyleProfileBrowseCard } from '@/types';

// ── Hook ────────────────────────────────────────────────────────────

// ── Modal ───────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const STYLE_INTELLIGENCE_PROJECT_EDIT_ROLES = new Set<string>([
  ProjectRole.OWNER,
  ProjectRole.ADMIN,
  ProjectRole.EDITOR,
]);
const STYLE_INTELLIGENCE_PROJECT_EDIT_PERMISSIONS = new Set<string>([
  ProjectPermission.WRITE,
  ProjectPermission.ADMIN,
]);

type StyleIntelligenceModalProps = Pick<UseStyleIntelligenceReturn, 'isOpen' | 'closeModal'>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getIdString(value: unknown): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;

  if (isRecord(value)) {
    for (const field of ['_id', '$oid', 'id']) {
      const id = getIdString(value[field]);
      if (id) return id;
    }
  }

  const stringified = String(value);
  return stringified && stringified !== '[object Object]' ? stringified : undefined;
}

function getCurrentUserId(user: unknown): string | undefined {
  if (user === null || typeof user !== 'object') return undefined;
  for (const field of ['id', '_id', 'userId']) {
    const value = getIdString(Reflect.get(user, field));
    if (value) return value;
  }
  return undefined;
}

function isActiveProjectMember(member: ProjectMember): boolean {
  return !member.status || member.status === 'active';
}

function hasProjectManageOrEditAccess(member: ProjectMember | undefined): boolean {
  if (!member || !isActiveProjectMember(member)) return false;
  if (STYLE_INTELLIGENCE_PROJECT_EDIT_ROLES.has(String(member.role).toLowerCase())) {
    return true;
  }
  return (member.permissions || []).some(permission =>
    STYLE_INTELLIGENCE_PROJECT_EDIT_PERMISSIONS.has(String(permission).toLowerCase()),
  );
}

export function StyleIntelligenceModal({ isOpen, closeModal }: StyleIntelligenceModalProps) {
  const [activeTab, setActiveTab] = useState<string>('upload');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  const {
    profiles, isAnalyzing, error,
    analyzeScreenshot, analyzeUrl, fetchProfiles,
    activateProfile, deactivateProfile, updateProfile, deleteProfile,
    sharedProfiles, sharedProfilesTotal, isBrowsing, browseSharedProfiles, importProfile,
  } = useStyleIntelligenceStore();

  const projectId = useEditorStore(s => s.project?.id);
  const currentProject = useEditorStore(s => s.project);
  const user = useAuthStore(s => s.user);
  const currentUserId = getCurrentUserId(user);
  const organizations = useOrganizationStore(s => s.organizations);
  const allOrganizations = useOrganizationStore(s => s.allOrganizations);
  const currentContext = useOrganizationStore(s => s.currentContext);
  const platformAdminEntitlement = useOrganizationStore(s => s.platformAdminEntitlement);
  const currentOrganization = useMemo(() => (
    currentContext?.type === 'organization'
      ? organizations.find(org => org._id === currentContext.id || org.slug === currentContext.orgSlug)
        ?? allOrganizations.find(org => org._id === currentContext.id || org.slug === currentContext.orgSlug)
      : undefined
  ), [allOrganizations, currentContext, organizations]);
  const isActiveOrganizationMembership = currentOrganization?.membership?.status === undefined
    || currentOrganization.membership.status === 'active';
  const canManageOrgWideProfiles = Boolean(
    platformAdminEntitlement
    || (isActiveOrganizationMembership && canManageStyleIntelligence(currentOrganization?.membership?.role)),
  );
  const isCurrentProjectOwnedByCurrentOrganization = Boolean(
    currentProject?.owner_type === 'Organization'
    && getIdString(currentProject.owner_id)
    && currentOrganization
    && getIdString(currentProject.owner_id) === currentOrganization._id,
  );
  const currentProjectMember = currentUserId
    ? currentProject?.members?.find(member => getIdString(member.userId) === currentUserId)
    : undefined;
  const hasCurrentProjectDirectAccess = Boolean(
    currentProject
    && currentUserId
    && (
      getIdString(currentProject.ownerId) === currentUserId
      || hasProjectManageOrEditAccess(currentProjectMember)
    ),
  );
  const hasStyleIntelligenceProjectAccess = Boolean(
    isCurrentProjectOwnedByCurrentOrganization
    && (
      platformAdminEntitlement
      || canManageOrgWideProfiles
      || hasCurrentProjectDirectAccess
    ),
  );
  const canActivateCurrentScope = projectId
    ? hasStyleIntelligenceProjectAccess
    : canManageOrgWideProfiles;

  const handleAnalyzeScreenshot = useCallback(
    (image: string, mimeType: string, name?: string) => analyzeScreenshot(image, mimeType, name),
    [analyzeScreenshot],
  );
  const handleAnalyzeUrl = useCallback(
    (url: string, name?: string) => analyzeUrl(url, name),
    [analyzeUrl],
  );

  // Wrap activate/deactivate to include projectId
  const handleActivate = useCallback(async (id: string) => {
    const warnings = await activateProfile(id, projectId || undefined);
    showActivationWarnings(warnings);
  }, [activateProfile, projectId]);
  const handleDeactivate = useCallback(
    (id: string) => deactivateProfile(id, projectId || undefined),
    [deactivateProfile, projectId],
  );

  // Derive active profiles for the current scope. Project-specific styles override
  // the org-wide profile, which remains the baseline.
  const orgWideActiveProfile = useMemo(() => (
    profiles.find(p => p.status === 'active' && (p.activeProjectIds?.length ?? 0) === 0) || null
  ), [profiles]);

  const projectActiveProfile = useMemo(() => {
    if (!projectId) return orgWideActiveProfile;
    return profiles.find(p => p.activeProjectIds?.includes(projectId)) || null;
  }, [profiles, projectId, orgWideActiveProfile]);

  const effectiveActiveProfile = projectActiveProfile || orgWideActiveProfile;

  // Fetch profiles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      setSelectedProfileId(null);
    }
  }, [isOpen, fetchProfiles]);

  // Show error toast
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const selectedProfile = profiles.find(p => p.profileId === selectedProfileId) || null;
  const selectedProfileCreatedByCurrentUser = Boolean(
    selectedProfile?.createdBy
    && currentUserId
    && selectedProfile.createdBy === currentUserId,
  );
  const selectedProfileActiveProjectIds = selectedProfile?.activeProjectIds || [];
  const selectedProfileIsProjectScopedActive = selectedProfile?.status === 'active' && selectedProfileActiveProjectIds.length > 0;
  const selectedProfileIsActiveInCurrentProject = Boolean(projectId && selectedProfileActiveProjectIds.includes(projectId));
  const selectedProfileIsSingleProjectActive = selectedProfileActiveProjectIds.length === 1;
  const selectedProfileIsOrgWideActive = selectedProfile?.status === 'active' && selectedProfileActiveProjectIds.length === 0;
  const canEditSelectedProfile = Boolean(selectedProfile && (
    canManageOrgWideProfiles
    || (
      selectedProfileIsProjectScopedActive
        ? selectedProfileIsSingleProjectActive && selectedProfileIsActiveInCurrentProject && hasStyleIntelligenceProjectAccess
        : selectedProfileCreatedByCurrentUser && !selectedProfileIsOrgWideActive
    )
  ));
  const canShareSelectedProfile = Boolean(selectedProfile && selectedProfileCreatedByCurrentUser);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent variant="light" className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            Style Intelligence
            {effectiveActiveProfile && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${FOREST_GREEN.DEFAULT}15`,
                  color: FOREST_GREEN.DEFAULT,
                }}
              >
                Active: {effectiveActiveProfile.name}
              </span>
            )}
            {projectId && projectActiveProfile && orgWideActiveProfile && orgWideActiveProfile.profileId !== projectActiveProfile.profileId && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                Baseline: {orgWideActiveProfile.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Create, browse, edit, and activate Style Intelligence profiles for the current organization or project.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 pb-4">
          {selectedProfile ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="mb-3"
                onClick={() => setSelectedProfileId(null)}
              >
                &larr; Back to profiles
              </Button>
              <StyleProfileEditor
                profile={selectedProfile}
                onSave={updateProfile}
                onActivate={handleActivate}
                projectId={projectId || undefined}
                hasOrgWideBaseline={Boolean(orgWideActiveProfile)}
                canActivate={canActivateCurrentScope}
                canEdit={canEditSelectedProfile}
                canShare={canShareSelectedProfile}
              />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="upload" className="flex-1 gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> Upload
                </TabsTrigger>
                <TabsTrigger value="url" className="flex-1 gap-1.5">
                  <Globe className="w-3.5 h-3.5" /> URL
                </TabsTrigger>
                <TabsTrigger value="profiles" className="flex-1 gap-1.5">
                  <Layers className="w-3.5 h-3.5" /> Profiles ({profiles.length})
                </TabsTrigger>
                <TabsTrigger value="browse" className="flex-1 gap-1.5">
                  <Search className="w-3.5 h-3.5" /> Browse
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="mt-4">
                <ScreenshotUploadTab isAnalyzing={isAnalyzing} onAnalyze={handleAnalyzeScreenshot} />
              </TabsContent>

              <TabsContent value="url" className="mt-4">
                <UrlAnalysisTab isAnalyzing={isAnalyzing} onAnalyze={handleAnalyzeUrl} />
              </TabsContent>

              <TabsContent value="profiles" className="mt-4">
                <ProfilesListTab
                  profiles={profiles}
                  projectId={projectId || undefined}
                  onSelect={(id) => setSelectedProfileId(id)}
                  onActivate={handleActivate}
                  onDeactivate={handleDeactivate}
                  onDelete={deleteProfile}
                  canManageOrgWideProfiles={canManageOrgWideProfiles}
                  canActivateProjectScopedProfiles={Boolean(projectId) && hasStyleIntelligenceProjectAccess}
                  sharedProfiles={sharedProfiles}
                  isBrowsing={isBrowsing}
                  onBrowse={browseSharedProfiles}
                  onImport={importProfile}
                  onImported={() => { fetchProfiles(); setActiveTab('profiles'); }}
                />
              </TabsContent>

              <TabsContent value="browse" className="mt-4">
                <BrowseTab
                  sharedProfiles={sharedProfiles}
                  sharedProfilesTotal={sharedProfilesTotal}
                  isBrowsing={isBrowsing}
                  onBrowse={browseSharedProfiles}
                  onImport={importProfile}
                  onImported={() => setActiveTab('profiles')}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={closeModal}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Tab 1: Screenshot Upload ────────────────────────────────────────

function ScreenshotUploadTab({
  isAnalyzing,
  onAnalyze,
}: {
  isAnalyzing: boolean;
  onAnalyze: (image: string, mimeType: string, name?: string) => Promise<void>;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup Object URL on unmount to prevent memory leak (B2)
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  /** Resize to max 1920x1080, JPEG 0.85 via async canvas.toBlob() */
  const processImage = useCallback((file: File): Promise<{ base64: string; mimeType: string }> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        const maxW = 1920, maxH = 1080;
        if (width > maxW || height > maxH) {
          const ratio = Math.min(maxW / width, maxH / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas context unavailable')); return; }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error('Compression failed')); return; }
            const reader = new FileReader();
            reader.onloadend = () => {
              const dataUrl = reader.result;
              if (typeof dataUrl !== 'string') {
                reject(new Error('Compression failed'));
                return;
              }
              const base64 = dataUrl.split(',')[1];
              if (!base64) {
                reject(new Error('Compression failed'));
                return;
              }
              resolve({ base64, mimeType: 'image/jpeg' });
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          0.85
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
      img.src = objectUrl;
    });
  }, []);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large — maximum 20MB');
      return;
    }
    setFileName(file.name.replace(/\.[^.]+$/, ''));
    setPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!preview) return;
    const file = fileInputRef.current?.files?.[0];
    if (!file) { toast.error('No file selected'); return; }

    let processedImage: { base64: string; mimeType: string };
    try {
      processedImage = await processImage(file);
    } catch {
      toast.error('Failed to process image');
      return;
    }

    try {
      await onAnalyze(processedImage.base64, processedImage.mimeType, fileName || undefined);
      toast.success('Screenshot analyzed — profile created');
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      // Error toast is driven by the store error state.
    }
  }, [preview, fileName, processImage, onAnalyze]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const dt = new DataTransfer();
      dt.items.add(file);
      if (fileInputRef.current) fileInputRef.current.files = dt.files;
      handleFile(file);
    }
  }, [handleFile]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
          isDragging ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400",
        )}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragLeave={(e) => { e.stopPropagation(); setIsDragging(false); }}
        onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        {preview ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={preview}
              alt="Screenshot preview"
              className="max-h-48 rounded border object-contain"
            />
            <span className="text-sm text-muted-foreground">{fileName || 'Screenshot'}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <ImageIcon className="w-10 h-10 text-gray-400" />
            <p className="text-sm text-muted-foreground">
              Drag &amp; drop a screenshot, or click to browse
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, or WebP — resized to max 1920&times;1080
            </p>
          </div>
        )}
      </div>

      {preview && (
        <div className="flex gap-2">
          <Input
            placeholder="Profile name (optional)"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="flex-1"
          />
          <Button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
          >
            {isAnalyzing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
            ) : (
              'Analyze'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: URL Analysis ─────────────────────────────────────────────

function UrlAnalysisTab({
  isAnalyzing,
  onAnalyze,
}: {
  isAnalyzing: boolean;
  onAnalyze: (url: string, name?: string) => Promise<void>;
}) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  const handleAnalyze = useCallback(async () => {
    if (!url.trim()) { toast.error('Please enter a URL'); return; }
    try {
      await onAnalyze(url.trim(), name.trim() || undefined);
      toast.success('URL analyzed — profile created');
      setUrl('');
      setName('');
    } catch {
      // Error handled by store
    }
  }, [url, name, onAnalyze]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Label className="text-sm">Website URL</Label>
        <Input
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="mt-1"
          type="url"
        />
        <p className="text-xs text-muted-foreground mt-1">
          We&apos;ll crawl the page and extract CSS design tokens (colors, fonts, spacing)
        </p>
      </div>
      <div>
        <Label className="text-sm">Profile Name (optional)</Label>
        <Input
          placeholder="e.g., Stripe Homepage"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
        />
      </div>
      <Button
        onClick={handleAnalyze}
        disabled={isAnalyzing || !url.trim()}
        style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
        className="self-start"
      >
        {isAnalyzing ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Extracting Styles...</>
        ) : (
          <><Globe className="w-4 h-4" /> Analyze URL</>
        )}
      </Button>
    </div>
  );
}

// ── Tab 3: Profiles List ────────────────────────────────────────────

type ProfilesFilter = 'mine' | 'shared-with-me' | 'my-shared';
type BrowseSharedProfilesParams = {
  search?: string;
  tags?: string;
  sort?: string;
  page?: number;
  limit?: number;
  append?: boolean;
};

type ProfileSwatch = { key: 'primary' | 'secondary' | 'accent'; color: string };

function getProfileSwatches(colors: { primary?: string; secondary?: string; accent?: string } | undefined): ProfileSwatch[] {
  const swatchCandidates: Array<{ key: ProfileSwatch['key']; color?: string }> = [
    { key: 'primary', color: colors?.primary },
    { key: 'secondary', color: colors?.secondary },
    { key: 'accent', color: colors?.accent },
  ];

  return swatchCandidates.flatMap((swatch): ProfileSwatch[] =>
    swatch.color ? [{ key: swatch.key, color: swatch.color }] : [],
  );
}

function showActivationWarnings(warnings: string[]): void {
  if (warnings.length === 0) return;

  toast.warning(warnings.length === 1 ? 'Profile activated with a warning' : 'Profile activated with warnings', {
    description: warnings.join('\n'),
  });
}

function ProfilesListTab({
  profiles,
  projectId,
  onSelect,
  onActivate,
  onDeactivate,
  onDelete,
  canManageOrgWideProfiles,
  canActivateProjectScopedProfiles,
  sharedProfiles,
  isBrowsing,
  onBrowse,
  onImport,
  onImported,
}: {
  profiles: IStyleProfile[];
  projectId?: string;
  onSelect: (id: string) => void;
  onActivate: (id: string) => Promise<void>;
  onDeactivate: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  canManageOrgWideProfiles: boolean;
  canActivateProjectScopedProfiles: boolean;
  sharedProfiles: StyleProfileBrowseCard[];
  isBrowsing: boolean;
  onBrowse: (params?: BrowseSharedProfilesParams) => Promise<void>;
  onImport: (id: string) => Promise<IStyleProfile | null>;
  onImported: () => void;
}) {
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filter, setFilter] = useState<ProfilesFilter>('mine');
  const [importing, setImporting] = useState<string | null>(null);
  const sharedLoaded = useRef(false);

  // Load shared-with-me profiles on first switch
  useEffect(() => {
    if (filter === 'shared-with-me' && !sharedLoaded.current) {
      sharedLoaded.current = true;
      onBrowse({ sort: 'recent' });
    }
  }, [filter, onBrowse]);

  const isActiveForProject = useCallback((profile: IStyleProfile) => {
    if (projectId) return profile.activeProjectIds?.includes(projectId) ?? false;
    return profile.status === 'active' && (profile.activeProjectIds?.length ?? 0) === 0;
  }, [projectId]);

  const isOrgWideBaseline = useCallback((profile: IStyleProfile) => (
    profile.status === 'active' && (profile.activeProjectIds?.length ?? 0) === 0
  ), []);
  const canActivateCurrentScope = projectId
    ? canActivateProjectScopedProfiles
    : canManageOrgWideProfiles;

  const statusColors: Record<string, { bg: string; text: string }> = {
    active: { bg: `${FOREST_GREEN.DEFAULT}15`, text: FOREST_GREEN.DEFAULT },
    draft: { bg: '#f3f4f6', text: MID_GRAY.DEFAULT },
    archived: { bg: '#fef3c7', text: '#92400e' },
  };

  const visibilityIcon = (v?: string) => {
    if (v === 'public') return <Globe className="w-3 h-3" />;
    if (v === 'shared') return <Users className="w-3 h-3" />;
    return <Lock className="w-3 h-3" />;
  };

  // Filter profiles
  const mySharedProfiles = useMemo(
    () => profiles.filter(p => p.visibility && p.visibility !== 'private'),
    [profiles],
  );

  const displayProfiles = filter === 'my-shared' ? mySharedProfiles : profiles;

  const handleImport = useCallback(async (profile: StyleProfileBrowseCard) => {
    setImporting(profile.profileId);
    try {
      const result = await onImport(profile.profileId);
      if (result) {
        toast.success('Profile imported!');
        onImported();
      }
    } catch {
      toast.error('Failed to import profile');
    } finally {
      setImporting(null);
    }
  }, [onImport, onImported]);

  const handleActivateClick = useCallback((id: string) => {
    void onActivate(id).catch(() => {
      // Error toast is driven by the store error state.
    });
  }, [onActivate]);

  const handleDeactivateClick = useCallback((id: string) => {
    void onDeactivate(id).catch(() => {
      // Error toast is driven by the store error state.
    });
  }, [onDeactivate]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargetId || isDeleting) return;

    setIsDeleting(true);
    try {
      await onDelete(deleteTargetId);
      toast.success('Style profile deleted');
      setDeleteTargetId(null);
    } catch {
      // Error toast is driven by the store error state.
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTargetId, isDeleting, onDelete]);

  return (
    <div className="flex flex-col gap-3">
      {/* Filter toggle */}
      <div className="flex rounded-md border overflow-hidden">
        {([
          { value: 'mine' as const, label: 'My Profiles', icon: Layers },
          { value: 'shared-with-me' as const, label: 'Shared with me', icon: Download },
          { value: 'my-shared' as const, label: 'My Shared', icon: Share2 },
        ]).map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={cn(
              'flex-1 px-2.5 py-1.5 text-xs flex items-center justify-center gap-1 transition-colors',
              filter === opt.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
              opt.value !== 'mine' && 'border-l',
            )}
          >
            <opt.icon className="w-3 h-3" /> {opt.label}
          </button>
        ))}
      </div>

      {/* Shared with me section */}
      {filter === 'shared-with-me' ? (
        isBrowsing && sharedProfiles.length === 0 ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-2">Loading shared profiles...</p>
          </div>
        ) : sharedProfiles.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No profiles shared with your organization yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sharedProfiles.map((profile) => (
              <div
                key={profile.profileId}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex -space-x-1 shrink-0">
                    {getProfileSwatches(profile.colors)
                      .map(({ key, color }) => (
                        <div
                          key={key}
                          className="w-5 h-5 rounded-full border-2 border-white"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{profile.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {profile.organizationName || 'Community'}
                      {profile.description && ` · ${profile.description}`}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleImport(profile)}
                  disabled={importing === profile.profileId}
                  className="text-xs shrink-0"
                >
                  {importing === profile.profileId ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <><Download className="w-3.5 h-3.5" /> Import</>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )
      ) : (
        /* My Profiles / My Shared section */
        displayProfiles.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Layers className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">
              {filter === 'my-shared' ? 'No shared profiles yet' : 'No style profiles yet'}
            </p>
            <p className="text-xs mt-1">
              {filter === 'my-shared'
                ? 'Open a profile and use the Share button to share it'
                : 'Upload a screenshot or enter a URL to create one'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {displayProfiles.map((profile) => {
              const isActive = isActiveForProject(profile);
              const isBaseline = Boolean(projectId && !isActive && isOrgWideBaseline(profile));
              const colors = (isActive || isBaseline)
                ? statusColors.active
                : (profile.status === 'active' ? statusColors.draft : (statusColors[profile.status] || statusColors.draft));
              const statusLabel = isActive ? 'active' : (isBaseline ? 'baseline' : (profile.status === 'active' ? 'active elsewhere' : profile.status));
              return (
                <div
                  key={profile.profileId}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onSelect(profile.profileId)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex -space-x-1 shrink-0">
                      {getProfileSwatches(profile.colors)
                        .map(({ key, color }) => (
                          <div
                            key={key}
                            className="w-5 h-5 rounded-full border-2 border-white"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{profile.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {profile.source.type} · {new Date(profile.createdAt).toLocaleDateString()}
                        {profile.visibility && profile.visibility !== 'private' && (
                          <span className="inline-flex items-center gap-0.5 text-muted-foreground">
                            · {visibilityIcon(profile.visibility)} {profile.visibility}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: colors.bg, color: colors.text }}
                    >
                      {statusLabel}
                    </span>

                    {canActivateCurrentScope && isActive ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDeactivateClick(profile.profileId)}
                        title="Deactivate"
                      >
                        <PowerOff className="w-3.5 h-3.5" />
                      </Button>
                    ) : isBaseline ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-60"
                        disabled
                        title="Already active as the org-wide baseline"
                      >
                        <Power className="w-3.5 h-3.5" style={{ color: FOREST_GREEN.DEFAULT }} />
                      </Button>
                    ) : canActivateCurrentScope ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleActivateClick(profile.profileId)}
                        title="Activate"
                      >
                        <Power className="w-3.5 h-3.5" style={{ color: FOREST_GREEN.DEFAULT }} />
                      </Button>
                    ) : null}
                    {canManageOrgWideProfiles && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setDeleteTargetId(profile.profileId)}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      <AlertDialog
        open={!!deleteTargetId}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Style Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the style profile and its extracted design tokens.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmDelete();
              }}
            >
              {isDeleting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Tab 4: Browse Shared Profiles ──────────────────────────────────

function BrowseTab({
  sharedProfiles,
  sharedProfilesTotal,
  isBrowsing,
  onBrowse,
  onImport,
  onImported,
}: {
  sharedProfiles: StyleProfileBrowseCard[];
  sharedProfilesTotal: number;
  isBrowsing: boolean;
  onBrowse: (params?: BrowseSharedProfilesParams) => Promise<void>;
  onImport: (id: string) => Promise<IStyleProfile | null>;
  onImported: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<'popular' | 'recent'>('popular');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const doSearch = useCallback((query: string, sortBy: string, pg: number, append = false) => {
    setHasSearched(true);
    onBrowse({
      search: query || undefined,
      sort: sortBy,
      page: pg,
      append,
    });
  }, [onBrowse]);

  // Initial load + debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      doSearch(searchQuery, sort, 1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, sort, doSearch]);

  const handleImport = useCallback(async (profile: StyleProfileBrowseCard) => {
    setImporting(profile.profileId);
    try {
      const result = await onImport(profile.profileId);
      if (result) {
        toast.success('Profile imported! You can now edit and activate it.');
        onImported();
      }
    } catch {
      toast.error('Failed to import profile');
    } finally {
      setImporting(null);
    }
  }, [onImport, onImported]);

  const hasMore = sharedProfiles.length < sharedProfilesTotal;

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    doSearch(searchQuery, sort, nextPage, true);
  }, [page, searchQuery, sort, doSearch]);

  return (
    <div className="flex flex-col gap-4">
      {/* Search & Sort */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search shared profiles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex rounded-md border overflow-hidden shrink-0">
          <button
            onClick={() => setSort('popular')}
            className={cn(
              'px-3 py-2 text-xs flex items-center gap-1 transition-colors',
              sort === 'popular' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
            )}
          >
            <TrendingUp className="w-3 h-3" /> Popular
          </button>
          <button
            onClick={() => setSort('recent')}
            className={cn(
              'px-3 py-2 text-xs flex items-center gap-1 transition-colors border-l',
              sort === 'recent' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
            )}
          >
            <Clock className="w-3 h-3" /> Recent
          </button>
        </div>
      </div>

      {/* Results */}
      {isBrowsing && sharedProfiles.length === 0 ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">Searching shared profiles...</p>
        </div>
      ) : sharedProfiles.length === 0 && hasSearched ? (
        <div className="text-center py-8 text-muted-foreground">
          <Globe className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No shared profiles found</p>
          <p className="text-xs mt-1">Try a different search or check back later</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {sharedProfiles.map((profile) => (
            <div
              key={profile.profileId}
              className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex -space-x-1 shrink-0">
                  {getProfileSwatches(profile.colors)
                    .map(({ key, color }) => (
                      <div
                        key={key}
                        className="w-5 h-5 rounded-full border-2 border-white"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{profile.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {profile.organizationName || 'Community'}
                    {profile.description && ` · ${profile.description}`}
                  </p>
                  {profile.tags && profile.tags.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {profile.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-muted rounded">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {(profile.importCount ?? 0) > 0 && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                    <Download className="w-3 h-3" /> {profile.importCount}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleImport(profile)}
                  disabled={importing === profile.profileId}
                  className="text-xs"
                >
                  {importing === profile.profileId ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <><Download className="w-3.5 h-3.5" /> Import</>
                  )}
                </Button>
              </div>
            </div>
          ))}

          {hasMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadMore}
              disabled={isBrowsing}
              className="self-center mt-2"
            >
              {isBrowsing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load more'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
